import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../emailModal/styles.css';
import './styles.css';
import { EMAIL_FORMAT_ERROR_MESSAGE } from '../errorMessages/errorMessages.constants';

const SCOPE_FIRST_PAGE = 'firstPage';
const SCOPE_ALL = 'all';
const METHOD_EMAIL = 'email';
const METHOD_PRINT = 'print';

const PrintEmailModal = ({
  isOpen,
  onClose,
  onSendEmail, // (emailAddress, scope) => void — results.jsx's handleEmailResults
  onPrint, // (scope) => void — results.jsx's handlePrintResults
  loading = false,
  error = '',
  success = false,
  contactUsUrl = '',
  totalResults = 200
}) => {
  const [scope, setScope] = useState(SCOPE_FIRST_PAGE);
  const [method, setMethod] = useState(METHOD_EMAIL);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  const handleClose = () => {
    if (loading) return;
    setScope(SCOPE_FIRST_PAGE);
    setMethod(METHOD_EMAIL);
    setEmail('');
    setEmailError('');
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocusedElement = document.activeElement;
    const focusTimer = setTimeout(() => closeButtonRef.current?.focus(), 100);

    const handleKeyDown = (e) => {
      if (!modalRef.current) return;
      if (e.key === 'Tab') {
        const focusable = modalRef.current.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      if (e.key === 'Escape' && !loading) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loading]);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (method === METHOD_EMAIL) {
      if (!email.trim()) {
        setEmailError('Email address is required');
        return;
      }
      if (!validateEmail(email.trim())) {
        setEmailError(EMAIL_FORMAT_ERROR_MESSAGE);
        return;
      }
      onSendEmail?.(email.trim(), scope);
    } else {
      // Print goes through the same handler as email — results.jsx's handleEmailResults
      // treats the special "print" value as "no email address, print delivery".
      onSendEmail?.('print', scope);
    }
  };

  if (!isOpen) return null;

  const title = success
    ? 'We got your message.'
    : error
    ? "We didn't get your message"
    : 'Send my search results';

  const sendLabel = method === METHOD_EMAIL ? 'Send Email' : 'Send to printer';

  return createPortal(
    <div
      className="email-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="print-email-modal-title"
    >
      <div
        className="email-modal-content"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div className="email-modal-header">
          <h3 id="print-email-modal-title">{title}</h3>
          <button
            ref={closeButtonRef}
            type="button"
            className="email-modal-close"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        <div className="email-modal-body">
          {success ? (
            <div className="email-result-body">
              {method === METHOD_PRINT ? (
                <p>Your results were sent to the printer. It may take a few minutes to arrive.</p>
              ) : (
                <>
                  <p>Check your email for info about these providers. It may take a few minutes to arrive.</p>
                  <p>Don't see the email? Check your spam or junk folder.</p>
                </>
              )}
            </div>
          ) : error ? (
            <div className="email-result-body">
              <p>
                {error}{' '}
                {contactUsUrl ? (
                  <a
                    href={contactUsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="email-contact-link"
                  >
                    Contact us
                  </a>
                ) : (
                  'Contact us'
                )}.
              </p>
              {onPrint && (
                <button
                  type="button"
                  className="email-send-button"
                  onClick={() => { onPrint(scope); handleClose(); }}
                >
                  Download / Print results instead
                </button>
              )}
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="email-form">
            <fieldset className="print-email-fieldset print-email-fieldset--inline">
              <label className="print-email-legend">Send me:</label>
              <label className="print-email-radio">
                <input
                  type="radio"
                  name="print-email-scope"
                  value={SCOPE_FIRST_PAGE}
                  checked={scope === SCOPE_FIRST_PAGE}
                  onChange={() => setScope(SCOPE_FIRST_PAGE)}
                  disabled={loading}
                />
                <span>First page only</span>
              </label>
              <label className="print-email-radio">
                <input
                  type="radio"
                  name="print-email-scope"
                  value={SCOPE_ALL}
                  checked={scope === SCOPE_ALL}
                  onChange={() => setScope(SCOPE_ALL)}
                  disabled={loading}
                />
                <span>All results (max:200)</span>
              </label>
            </fieldset>

            <fieldset className="print-email-fieldset  print-email-fieldset--inline">
              <label className="print-email-legend">I would like to receive my results by</label>
              <label className="print-email-radio">
                <input
                  type="radio"
                  name="print-email-method"
                  value={METHOD_EMAIL}
                  checked={method === METHOD_EMAIL}
                  onChange={() => setMethod(METHOD_EMAIL)}
                  disabled={loading}
                />
                <span>Email</span>
              </label>
              <label className="print-email-radio">
                <input
                  type="radio"
                  name="print-email-method"
                  value={METHOD_PRINT}
                  checked={method === METHOD_PRINT}
                  onChange={() => {
                    setMethod(METHOD_PRINT);
                    setEmailError('');
                  }}
                  disabled={loading}
                />
                <span>Print</span>
              </label>
            </fieldset>

            {/* An address is only needed when emailing. */}
            {method === METHOD_EMAIL && (
              <div className="email-input-group">
                <label htmlFor="print-email-input" className="email-label">
                  Email
                </label>
                <p className="email-input-hint">Fill in your email address like this: example@domain.com</p>
                <input
                  id="print-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  placeholder="Enter your email address"
                  className={`email-input ${emailError ? 'error' : ''}`}
                  disabled={loading}
                  aria-required="true"
                  aria-invalid={emailError ? 'true' : 'false'}
                  aria-describedby={emailError ? 'print-email-error' : undefined}
                />
                {emailError && (
                  <span id="print-email-error" className="email-error-message" role="alert">
                    {emailError}
                  </span>
                )}
              </div>
            )}

            <button
              type="submit"
              className="email-send-button"
              disabled={loading || (method === METHOD_EMAIL && !email.trim())}
            >
              {loading ? (
                <>
                  <span className="email-loading-spinner"></span>
                  Sending...
                </>
              ) : (
                sendLabel
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrintEmailModal;
